/**
 * Request Interceptor
 * Provides rate limiting, request deduplication, and unified error handling
 */

import { logger } from './Logger';
import { ServiceError } from './ServiceError';

export interface RateLimitConfig {
  maxRequests: number;
  windowMs: number;
  blockDurationMs?: number;
}

export interface InterceptorConfig {
  rateLimit?: RateLimitConfig;
  enableDeduplication?: boolean;
  enableRetry?: boolean;
  maxRetries?: number;
  retryDelay?: number;
  timeout?: number;
}

interface RequestRecord {
  count: number;
  windowStart: number;
  blocked?: boolean;
  blockUntil?: number;
}

interface PendingRequest {
  promise: Promise<any>;
  timestamp: number;
}

export class RequestInterceptor {
  private static instance: RequestInterceptor;
  private readonly log = logger.createServiceLogger('RequestInterceptor');

  // Rate limiting
  private rateLimitRecords = new Map<string, RequestRecord>();

  // Request deduplication
  private pendingRequests = new Map<string, PendingRequest>();

  // Configuration
  private readonly config: Required<InterceptorConfig> = {
    rateLimit: {
      maxRequests: 100,
      windowMs: 60000, // 1 minute
      blockDurationMs: 300000, // 5 minutes
    },
    enableDeduplication: true,
    enableRetry: true,
    maxRetries: 3,
    retryDelay: 1000,
    timeout: 30000,
  };

  private constructor(config?: InterceptorConfig) {
    if (config) {
      this.config = {
        ...this.config,
        ...config,
        rateLimit: config.rateLimit
          ? { ...this.config.rateLimit, ...config.rateLimit }
          : this.config.rateLimit,
      };
    }

    // Cleanup old records periodically
    setInterval(() => this.cleanupOldRecords(), 60000); // Every minute
  }

  static getInstance(config?: InterceptorConfig): RequestInterceptor {
    if (!RequestInterceptor.instance) {
      RequestInterceptor.instance = new RequestInterceptor(config);
    }
    return RequestInterceptor.instance;
  }

  /**
   * Intercept and process a request
   */
  async intercept<T>(
    key: string,
    request: () => Promise<T>,
    options?: Partial<InterceptorConfig>,
  ): Promise<T> {
    const config = { ...this.config, ...options };

    // Check rate limit
    if (config.rateLimit) {
      await this.checkRateLimit(key, config.rateLimit);
    }

    // Check for duplicate requests
    if (config.enableDeduplication) {
      const existing = this.pendingRequests.get(key);
      if (existing && Date.now() - existing.timestamp < 5000) {
        // 5 second window
        this.log.debug('Deduplicating request', { key });
        return existing.promise;
      }
    }

    // Create request promise with timeout and retry
    const requestPromise = this.executeWithTimeoutAndRetry(
      request,
      config.timeout!,
      config.enableRetry!,
      config.maxRetries!,
      config.retryDelay!,
    );

    // Store for deduplication
    if (config.enableDeduplication) {
      this.pendingRequests.set(key, {
        promise: requestPromise,
        timestamp: Date.now(),
      });
    }

    try {
      const result = await requestPromise;

      // Clean up pending request
      if (config.enableDeduplication) {
        this.pendingRequests.delete(key);
      }

      return result;
    } catch (error) {
      // Clean up pending request
      if (config.enableDeduplication) {
        this.pendingRequests.delete(key);
      }

      throw error;
    }
  }

  /**
   * Check rate limit for a key
   */
  private async checkRateLimit(key: string, config: RateLimitConfig): Promise<void> {
    const now = Date.now();
    const record = this.rateLimitRecords.get(key);

    // Check if blocked
    if (record?.blocked && record.blockUntil && now < record.blockUntil) {
      const remainingMs = record.blockUntil - now;
      this.log.warn('Rate limit blocked', { key, remainingMs });
      throw new ServiceError(
        'RATE_LIMIT_BLOCKED',
        `Rate limit exceeded. Blocked for ${Math.ceil(remainingMs / 1000)} seconds`,
        429,
      );
    }

    // Initialize or reset record
    if (!record || now - record.windowStart > config.windowMs) {
      this.rateLimitRecords.set(key, {
        count: 1,
        windowStart: now,
        blocked: false,
      });
      return;
    }

    // Increment count
    record.count++;

    // Check if exceeded
    if (record.count > config.maxRequests) {
      record.blocked = true;
      record.blockUntil = now + (config.blockDurationMs || 300000);

      this.log.error('Rate limit exceeded', undefined, {
        key,
        count: record.count,
        maxRequests: config.maxRequests,
      });

      throw new ServiceError(
        'RATE_LIMIT_EXCEEDED',
        `Rate limit exceeded. Maximum ${config.maxRequests} requests per ${config.windowMs / 1000} seconds`,
        429,
      );
    }
  }

  /**
   * Execute request with timeout and retry
   */
  private async executeWithTimeoutAndRetry<T>(
    request: () => Promise<T>,
    timeout: number,
    enableRetry: boolean,
    maxRetries: number,
    retryDelay: number,
  ): Promise<T> {
    let lastError: Error | undefined;

    for (let attempt = 0; attempt <= (enableRetry ? maxRetries : 0); attempt++) {
      try {
        // Create timeout promise
        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(() => {
            reject(
              new ServiceError('REQUEST_TIMEOUT', `Request timed out after ${timeout}ms`, 408),
            );
          }, timeout);
        });

        // Race between request and timeout
        const result = await Promise.race([request(), timeoutPromise]);

        return result;
      } catch (error) {
        lastError = error as Error;

        // Don't retry on client errors (4xx) except 429
        if (
          error instanceof ServiceError &&
          error.code >= 400 &&
          error.code < 500 &&
          error.code !== 429
        ) {
          throw error;
        }

        // Don't retry on last attempt
        if (!enableRetry || attempt === maxRetries) {
          throw error;
        }

        // Calculate delay with exponential backoff
        const delay = retryDelay * Math.pow(2, attempt);

        this.log.warn('Request failed, retrying', {
          attempt: attempt + 1,
          maxRetries,
          delay,
          error: lastError.message,
        });

        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }

    throw lastError || new ServiceError('REQUEST_FAILED', 'Request failed after retries', 500);
  }

  /**
   * Clean up old records
   */
  private cleanupOldRecords(): void {
    const now = Date.now();
    let cleaned = 0;

    // Clean rate limit records
    for (const [key, record] of this.rateLimitRecords) {
      if (now - record.windowStart > this.config.rateLimit.windowMs * 2) {
        this.rateLimitRecords.delete(key);
        cleaned++;
      }
    }

    // Clean pending requests
    for (const [key, request] of this.pendingRequests) {
      if (now - request.timestamp > 60000) {
        // 1 minute old
        this.pendingRequests.delete(key);
        cleaned++;
      }
    }

    if (cleaned > 0) {
      this.log.debug('Cleaned up old records', { count: cleaned });
    }
  }

  /**
   * Get interceptor statistics
   */
  getStats() {
    return {
      rateLimitRecords: this.rateLimitRecords.size,
      pendingRequests: this.pendingRequests.size,
      blockedKeys: Array.from(this.rateLimitRecords.values()).filter((r) => r.blocked).length,
    };
  }

  /**
   * Reset rate limit for a key
   */
  resetRateLimit(key: string): void {
    this.rateLimitRecords.delete(key);
    this.log.info('Rate limit reset', { key });
  }

  /**
   * Clear all records
   */
  clearAll(): void {
    this.rateLimitRecords.clear();
    this.pendingRequests.clear();
    this.log.info('All records cleared');
  }
}

// Export singleton instance
export const requestInterceptor = RequestInterceptor.getInstance();
